import { SparklesIcon } from '@heroicons/react/outline';
import dynamic from 'next/dynamic';
import type { FormEvent, KeyboardEvent, ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

import clsxm from '@/lib/clsxm';
import { isLotteryPreset } from '@/lib/lottery';
import { isPaidOnlyPreset } from '@/lib/presetAccess';

import {
  ArrowGlyph,
  buttonPrimary,
  tone,
} from '@/components/agents/primitives';
import Spinner from '@/components/Spinner';

import { getAiModelOptions } from '@/constants/aiModels';
import type { Preset } from '@/constants/presets';
import { useAnalytics } from '@/contexts/Analytics';

import {
  type TemplateExample,
  templateSignupHref,
  useTemplateTranslation,
} from './kit';
import OutputPanel from './OutputPanel';
import type { TemplateSampleSet } from './sampleSets';
import { usePublicGeneration } from './usePublicGeneration';
import { useTemplateFields } from './useTemplateFields';
import WorkspaceFields from './WorkspaceFields';
import {
  LanguagePill,
  ModelPicker,
  OutputsPill,
  WebSearchPill,
} from './WorkspaceOptions';

const PricingModalPublic = dynamic(
  () => import('@/components/PricingModalPublic'),
  { ssr: false },
);
const LotteryUpgradeModal = dynamic(
  () => import('@/components/pricing/LotteryUpgradeModal'),
  { ssr: false },
);

type Props = {
  preset: Preset;
  /** A curated example, shown in the output pane until the visitor runs it. */
  example?: TemplateExample | null;
  /** Finished visuals for templates that make cards, worksheets or slides. */
  samples?: TemplateSampleSet | null;
};

const actionClass = clsxm(
  buttonPrimary,
  'h-12 w-full py-0 sm:w-auto sm:px-7 disabled:cursor-wait disabled:opacity-80',
);

/**
 * The tool itself, front and centre: fields on the left, output on the
 * right. Free templates generate right here; sign-up-only templates take
 * the same inputs and carry them over to the tool after registration.
 */
export default function TemplateWorkspace(props: Props) {
  return props.preset.isFree ? (
    <FreeWorkspace {...props} />
  ) : (
    <GatedWorkspace {...props} />
  );
}

function Shell({ left, right }: { left: ReactNode; right: ReactNode }) {
  return (
    <div className='rounded-[28px] bg-[#ececfa] p-1.5 ring-1 ring-inset ring-[#0f0f28]/[0.04] sm:rounded-[34px] sm:p-2.5 dark:bg-[#17162c] dark:ring-white/[0.06]'>
      <div
        className={clsxm(
          'overflow-hidden rounded-[22px] shadow-[0_30px_60px_-30px_rgba(30,27,75,0.35),0_1px_2px_rgba(15,15,40,0.05)] sm:rounded-[28px]',
          tone.surface,
          tone.line,
        )}
      >
        <div className='grid lg:grid-cols-2'>
          {left}
          {right}
        </div>
      </div>
    </div>
  );
}

/** "⌘ ↵" on a Mac, "Ctrl ↵" elsewhere; nothing until we know which. */
function useShortcutLabel() {
  const [label, setLabel] = useState<string | null>(null);
  useEffect(() => {
    const platform =
      (navigator as Navigator & { userAgentData?: { platform?: string } })
        .userAgentData?.platform || navigator.platform;
    setLabel(/mac|iphone|ipad/i.test(platform) ? '⌘ ↵' : 'Ctrl ↵');
  }, []);
  return label;
}

function FreeWorkspace({ preset, example, samples }: Props) {
  const { t } = useTemplateTranslation();
  const analytics = useAnalytics();
  const generation = usePublicGeneration(preset);
  const {
    fields,
    setField,
    captions,
    isGenerating,
    reachedTemplateLimit,
    reachedTotalLimit,
    runsLeft,
    shouldUpgrade,
    setShouldUpgrade,
  } = generation;
  const formRef = useRef<HTMLFormElement>(null);
  const outputRef = useRef<HTMLDivElement>(null);
  const shortcut = useShortcutLabel();
  const signupHref = templateSignupHref(preset);

  // Visitors always run the Standard model; the menu shows the full lineup.
  const modelOptions = useMemo(
    () => getAiModelOptions(preset, { includeLegacy: false }),
    [preset],
  );

  // Web search is paid, so for visitors the pill is an upsell. Mirrors the
  // dashboard's visibility rules (GenericComponent).
  const webSearchAvailable =
    Boolean(preset.shouldUseChatGPTAPI || preset.useGemini) &&
    !preset.useTool &&
    !preset.hideWebSearch &&
    !preset.fields?.some((field) => field.name === 'enableWebSearch');

  // What opened the upgrade window, for analytics. The generation hook opens
  // it on its own when the server answers with a daily limit.
  const [upgradeTrigger, setUpgradeTrigger] = useState('limit');
  const openUpgrade = (trigger: string) => {
    setUpgradeTrigger(trigger);
    setShouldUpgrade(true);
  };
  const closeUpgrade = () => {
    setShouldUpgrade(false);
    setUpgradeTrigger('limit');
  };
  const isLottery = isLotteryPreset(preset.slug);

  // The lottery window tracks its own opening.
  useEffect(() => {
    if (!shouldUpgrade || isLottery) return;
    analytics.track('Upgrade Window Shown', {
      preset: preset.slug,
      trigger: upgradeTrigger,
      window: 'plans',
      source: 'public',
      signed_in: false,
    });
    // Once per opening.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldUpgrade]);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (isGenerating) return;
    const started = await generation.generate();
    if (!started) return;
    const wide = window.matchMedia('(min-width: 1024px)').matches;
    outputRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: wide ? 'nearest' : 'start',
    });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLFormElement>) => {
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      formRef.current?.requestSubmit();
    }
  };

  let action: ReactNode;
  let note: string;
  if (isGenerating) {
    // Stays put until the result is in, even though the run already counts.
    action = (
      <button type='button' disabled className={actionClass}>
        <Spinner className='h-5 w-5 animate-spin text-white' />
        {t('Generating...')}
      </button>
    );
    note = t('This usually takes a few seconds.');
  } else if (reachedTotalLimit) {
    action = (
      <button
        type='button'
        onClick={() => openUpgrade('limit')}
        className={actionClass}
      >
        {t('Upgrade')}
        <ArrowGlyph />
      </button>
    );
    note = t('You’ve used today’s free generations. Upgrade to keep going.');
  } else if (reachedTemplateLimit) {
    action = isPaidOnlyPreset(preset) ? (
      <button
        type='button'
        onClick={() => openUpgrade('limit')}
        className={actionClass}
      >
        {t('Upgrade')}
        <ArrowGlyph />
      </button>
    ) : (
      <a href={signupHref} className={actionClass}>
        {t('Get started for free')}
        <ArrowGlyph />
      </a>
    );
    note = t('You’ve used today’s free generation for this tool.');
  } else {
    action = (
      <button type='submit' className={actionClass}>
        <SparklesIcon className='h-5 w-5' aria-hidden='true' />
        {t('Generate')}
        {shortcut ? (
          <kbd className='ms-1 hidden rounded-md bg-white/15 px-1.5 py-0.5 font-sans text-[11px] font-medium text-white/85 sm:inline'>
            {shortcut}
          </kbd>
        ) : null}
      </button>
    );
    note = `${t('Free generations left today: {{count}}', {
      count: runsLeft,
    })} · ${t('No sign-up needed')}`;
  }

  return (
    <>
      {shouldUpgrade ? (
        isLottery ? (
          <LotteryUpgradeModal
            isVisible={shouldUpgrade}
            onClose={closeUpgrade}
            preset={preset.slug}
            trigger={upgradeTrigger}
            source='public'
          />
        ) : (
          <PricingModalPublic
            isVisible={shouldUpgrade}
            onClose={closeUpgrade}
          />
        )
      ) : null}
      <Shell
        left={
          <form
            ref={formRef}
            onSubmit={onSubmit}
            onKeyDown={onKeyDown}
            noValidate
            className='flex flex-col p-5 sm:p-7 lg:p-8'
          >
            <WorkspaceFields
              preset={preset}
              fields={fields}
              setField={setField}
              uploads={{
                uploadedFiles: generation.uploadedFiles,
                uploadedMultiFiles: generation.uploadedMultiFiles,
                uploadFile: generation.uploadFile,
                removeFile: generation.removeFile,
                setMultiFiles: generation.setMultiFiles,
                isFileLoading: generation.isFileLoading,
              }}
            />

            {/* The lottery generators show none of these. */}
            <div className='mt-7 flex flex-wrap items-center gap-2 empty:hidden'>
              {preset.hideModelSelector ? null : (
                <ModelPicker
                  options={modelOptions}
                  value={modelOptions[0]}
                  onLockedPick={(option) => {
                    analytics.track('Public AI Model Upgrade Click', {
                      preset: preset.slug,
                      model: option.value,
                      source: 'public',
                    });
                    openUpgrade('model');
                  }}
                />
              )}
              {preset.hideLanguageSelector !== true ? (
                <LanguagePill
                  value={generation.language}
                  onChange={generation.setLanguage}
                />
              ) : null}
              {preset.outputs !== 1 ? (
                <OutputsPill
                  value={generation.numberOfOutputs}
                  max={generation.maxOutputs}
                  onChange={(count) => {
                    // More than one result at a time is a paid feature.
                    if (count > 1) {
                      openUpgrade('outputs');
                      return;
                    }
                    generation.setNumberOfOutputs(count);
                  }}
                />
              ) : null}
              {webSearchAvailable ? (
                <WebSearchPill
                  onClick={() => {
                    analytics.track('Public Web Search Upgrade Click', {
                      preset: preset.slug,
                      source: 'public',
                    });
                    openUpgrade('web-search');
                  }}
                />
              ) : null}
            </div>

            <div className='mt-7 border-t border-[#0f0f28]/[0.07] pt-6 dark:border-white/[0.08]'>
              <div className='flex flex-wrap items-center gap-x-5 gap-y-3'>
                {action}
                <button
                  type='button'
                  onClick={generation.clearInputs}
                  className={clsxm(
                    'mx-auto text-[14px] font-medium transition-colors hover:text-[#10101c] sm:mx-0 dark:hover:text-white',
                    tone.quiet,
                  )}
                >
                  {t('Clear inputs')}
                </button>
              </div>
              <p className={clsxm('mt-3 text-[13px] leading-5', tone.quiet)}>
                {note}
              </p>
            </div>
          </form>
        }
        right={
          <div ref={outputRef} className='flex scroll-mt-24 flex-col'>
            <OutputPanel
              preset={preset}
              example={example}
              samples={samples}
              captions={captions}
              limitReached={reachedTemplateLimit || reachedTotalLimit}
              signupHref={signupHref}
              onUpgradeClick={(trigger = 'result') => openUpgrade(trigger)}
              emptyHint={t('Fill in the details and click Generate.')}
              language={generation.language}
            />
          </div>
        }
      />
    </>
  );
}

/**
 * Sign-up-only templates: the same inputs, saved as the visitor types, and
 * one action that opens sign-up. GenericComponent restores the values in the
 * tool after registration, so nothing typed here is lost.
 */
function GatedWorkspace({ preset, example, samples }: Props) {
  const { t } = useTemplateTranslation();
  const analytics = useAnalytics();
  const { fields, setField } = useTemplateFields(preset);
  const signupHref = templateSignupHref(preset);

  const startSignup = () =>
    analytics.track('Public Gated Template Click', {
      preset: preset.slug,
      source: 'public',
    });

  return (
    <Shell
      left={
        <form
          onSubmit={(event) => {
            event.preventDefault();
            startSignup();
            window.location.assign(signupHref);
          }}
          noValidate
          className='flex flex-col p-5 sm:p-7 lg:p-8'
        >
          <WorkspaceFields
            preset={preset}
            fields={fields}
            setField={setField}
          />

          <div className='mt-7 border-t border-[#0f0f28]/[0.07] pt-6 dark:border-white/[0.08]'>
            <a href={signupHref} onClick={startSignup} className={actionClass}>
              <SparklesIcon className='h-5 w-5' aria-hidden='true' />
              {isPaidOnlyPreset(preset)
                ? t('Get started')
                : t('Get started for free')}
              <ArrowGlyph />
            </a>
            <p className={clsxm('mt-3 text-[13px] leading-5', tone.quiet)}>
              {isPaidOnlyPreset(preset)
                ? t(
                    'Included in paid plans. A free account can try it once. Anything you type here is saved for you.',
                  )
                : t(
                    'Free with an account. Anything you type here is saved and waiting for you after you sign up.',
                  )}
            </p>
          </div>
        </form>
      }
      right={
        <OutputPanel
          preset={preset}
          example={example}
          samples={samples}
          signupHref={signupHref}
          emptyHint={t('Sign up free to generate with this tool.')}
        />
      }
    />
  );
}
